import React, { useState } from 'react';
import {
  Box,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  Avatar,
  Tooltip,
  CircularProgress,
  CircularProgressLabel,
  Tag,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
} from '@chakra-ui/react';
import {
  Brain,
  GitBranch,
  Activity,
  BarChart2,
  MoreVertical,
  Eye,
  Play,
  Pause,
  RefreshCw,
  Download,
  FileText,
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  Zap,
} from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import { NexusAdminLayout } from '../../components/nexus/NexusAdminLayout';
import DashboardPanel from '../ui-samples/datascience/DashboardPanel';
import SystemConfigPanel from '../ui-samples/datascience/SystemConfigPanel';
import ModelTrainingPanel from '../ui-samples/datascience/ModelTrainingPanel';
import PipelineHealthPanel from '../ui-samples/datascience/PipelineHealthPanel';
import PerformancePanel from '../ui-samples/datascience/PerformancePanel';

// Sample data
const metrics = [
  {
    label: "Model Accuracy",
    value: "94.8%",
    change: "+2.3%",
    icon: Brain,
    color: "methodist.blue",
  },
  {
    label: "Pipeline Health",
    value: "98.2%",
    change: "+0.5%",
    icon: Activity,
    color: "methodist.green",
  },
  {
    label: "Active Models",
    value: "12",
    change: "+2",
    icon: GitBranch,
    color: "methodist.purple",
  },
];

const activeModels = [
  {
    id: "MODEL-001",
    name: "Stress Prediction v2.1",
    type: "Classification",
    status: "deployed",
    accuracy: 94.8,
    latency: "45ms",
    lastTrained: "2d ago",
    version: "2.1.0",
    author: "Dr. Sarah Chen",
    framework: "PyTorch",
  },
  {
    id: "MODEL-002",
    name: "Burnout Risk Analysis",
    type: "Regression",
    status: "training",
    accuracy: 91.2,
    latency: "38ms",
    lastTrained: "1h ago",
    version: "1.8.2",
    author: "Dr. Michael Patel",
    framework: "TensorFlow",
  },
  {
    id: "MODEL-003",
    name: "Wellness Score",
    type: "Multi-class",
    status: "evaluating",
    accuracy: 89.5,
    latency: "52ms",
    lastTrained: "5h ago",
    version: "3.0.1",
    author: "Dr. Emily Wong",
    framework: "scikit-learn",
  },
];

const dataPipelines = [
  {
    id: "PIPE-001",
    name: "Stress Metrics ETL",
    status: "running",
    health: 98,
    processedRecords: "1.2M",
    lastRun: "2m ago",
    nextRun: "28m",
    type: "Batch",
    source: "Wearable Devices",
  },
  {
    id: "PIPE-002",
    name: "Real-time Analytics",
    status: "warning",
    health: 82,
    processedRecords: "450K",
    lastRun: "1m ago",
    nextRun: "Real-time",
    type: "Streaming",
    source: "Mobile App",
  },
  {
    id: "PIPE-003",
    name: "Feedback Processing",
    status: "stopped",
    health: 0,
    processedRecords: "890K",
    lastRun: "2h ago",
    nextRun: "Manual",
    type: "Batch",
    source: "User Feedback",
  },
];

const DataScienceSlide = () => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bgColor = useColorModeValue('white', 'gray.800');
  const [activeTab, setActiveTab] = useState(0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'deployed': return 'green';
      case 'training': return 'blue';
      case 'evaluating': return 'orange';
      case 'failed': return 'red';
      case 'running': return 'green';
      case 'warning': return 'orange';
      case 'stopped': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'deployed': return CheckCircle;
      case 'training': return RefreshCw;
      case 'evaluating': return Brain;
      case 'failed': return AlertTriangle;
      case 'running': return Zap;
      case 'warning': return AlertTriangle;
      case 'stopped': return Pause;
      default: return Clock;
    }
  };

  const getHealthColor = (health: number) => {
    if (health >= 90) return 'green';
    if (health >= 70) return 'orange';
    return 'red';
  };

  return (
    <MethodistSlide title="Neural Lab">
      <NexusAdminLayout
        title="Neural Lab: Data Science Hub"
        activeSection="data-science"
        userName="Dr. Sarah Chen"
        userRole="Data Science Lead"
      >
        <Tabs variant="enclosed" colorScheme="purple" onChange={(index) => setActiveTab(index)} defaultIndex={0}>
          <TabList>
            <Tab><HStack><Icon as={Database} boxSize={4} /><Text>Overview</Text></HStack></Tab>
            <Tab><HStack><Icon as={Brain} boxSize={4} /><Text>Model Training</Text></HStack></Tab>
            <Tab><HStack><Icon as={Activity} boxSize={4} /><Text>Pipeline Health</Text></HStack></Tab>
            <Tab><HStack><Icon as={BarChart2} boxSize={4} /><Text>Performance</Text></HStack></Tab>
            <Tab><HStack><Icon as={Settings} boxSize={4} /><Text>System Config</Text></HStack></Tab>
          </TabList>
          
          <TabPanels>
            {/* Overview Panel */}
            <TabPanel p={0} pt={4}>
              <DashboardPanel />
            </TabPanel>
            
            {/* Model Training Panel */}
            <TabPanel p={0} pt={4}>
              <ModelTrainingPanel />
            </TabPanel>
            
            {/* Pipeline Health Panel */}
            <TabPanel p={0} pt={4}>
              <PipelineHealthPanel />
            </TabPanel>
            
            {/* Performance Panel */}
            <TabPanel p={0} pt={4}>
              <PerformancePanel />
            </TabPanel>
            
            {/* System Config Panel */}
            <TabPanel p={0} pt={4}>
              <SystemConfigPanel />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </NexusAdminLayout>
    </MethodistSlide>
  );
};

export default DataScienceSlide;
